import { currentItem } from './mailbox';

/**
 * Attachment metadata and byte retrieval.
 *
 * Kept behind this interface so a Graph-backed implementation could replace Office.js later
 * without touching the form. Attachment IDs are session-scoped - never persist them.
 */

export interface MailAttachment {
  /** Office.js attachment id. Session-scoped; do not store. */
  id: string;
  name: string;
  size: number;
  isInline: boolean;
  /** Present when Mailbox 1.16 is available; maps cid: references in the body. */
  contentId?: string;
}

export interface AttachmentBytes {
  name: string;
  contentType: string;
  /** Decoded binary. */
  bytes: ArrayBuffer;
}

const CONTENT_TIMEOUT_MS = 20_000;
const MAX_CONCURRENCY = 2;

function hasContentIdSupport(): boolean {
  // contentId is Mailbox 1.16. Both target clients have it; older hosts do not.
  try {
    return Office.context.requirements.isSetSupported('Mailbox', '1.16');
  } catch {
    return false;
  }
}

/** Sniff MIME from magic bytes. AttachmentDetails.contentType is deprecated. */
export function sniffMime(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  if (u8.length >= 3 && u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff) return 'image/jpeg';
  if (u8.length >= 8 && u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47) {
    return 'image/png';
  }
  if (u8.length >= 6 && u8[0] === 0x47 && u8[1] === 0x49 && u8[2] === 0x46) return 'image/gif';
  if (u8.length >= 4 && u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46) {
    return 'application/pdf';
  }
  if (
    u8.length >= 12 &&
    u8[0] === 0x52 &&
    u8[1] === 0x49 &&
    u8[2] === 0x46 &&
    u8[3] === 0x46 &&
    u8[8] === 0x57 &&
    u8[9] === 0x45 &&
    u8[10] === 0x42 &&
    u8[11] === 0x50
  ) {
    return 'image/webp';
  }
  return 'application/octet-stream';
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function listAttachments(): MailAttachment[] {
  const item = currentItem();
  if (!item) return [];

  const withCid = hasContentIdSupport();
  return (item.attachments ?? []).map((a) => {
    const att: MailAttachment = {
      id: a.id,
      name: a.name || 'attachment',
      size: a.size ?? 0,
      isInline: a.isInline === true,
    };
    if (withCid) {
      const cid = (a as Office.AttachmentDetails & { contentId?: string }).contentId;
      if (cid) att.contentId = cid;
    }
    return att;
  });
}

/**
 * Fetches one attachment's bytes. Empty content with Succeeded status is treated as
 * retryable - a confirmed Office.js bug under concurrency.
 */
export function getAttachmentContent(id: string, attempt = 0): Promise<AttachmentBytes> {
  const item = currentItem();
  if (!item) return Promise.reject(new Error('No message is open.'));

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Timed out reading an attachment.'));
    }, CONTENT_TIMEOUT_MS);

    item.getAttachmentContentAsync(id, async (result) => {
      clearTimeout(timer);
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(result.error ?? new Error('Could not read attachment content.'));
        return;
      }

      const value = result.value;
      const raw = value?.content ?? '';
      // Succeeded-but-empty is intermittent and reliably worse under concurrency. Retry a
      // couple of times before giving up.
      if (!raw) {
        if (attempt < 2) {
          try {
            resolve(await getAttachmentContent(id, attempt + 1));
          } catch (error) {
            reject(error);
          }
          return;
        }
        reject(new Error('Attachment content came back empty.'));
        return;
      }

      let bytes: ArrayBuffer;
      if (value.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
        bytes = base64ToArrayBuffer(raw);
      } else {
        // Url and Eml formats are rare for regular file attachments; treat as UTF-8 text.
        bytes = new TextEncoder().encode(raw).buffer;
      }

      const name =
        listAttachments().find((a) => a.id === id)?.name ?? 'attachment';
      resolve({ name, contentType: sniffMime(bytes), bytes });
    });
  });
}

/**
 * Runs async work with a concurrency cap. Office.js attachment reads fail under
 * Promise.all; two at a time is the documented safe ceiling.
 */
export async function mapLimited<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const i = next++;
      const item = items[i];
      if (item === undefined) continue;
      results[i] = await fn(item);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export const ATTACHMENT_CONCURRENCY = MAX_CONCURRENCY;

/**
 * Heuristic: small inline images are usually signature logos. Microsoft provides no
 * definitive signal, so the UI must keep this visible and reversible.
 */
export function likelySignatureImage(att: MailAttachment): boolean {
  if (!att.isInline) return false;
  // Under ~40 KB and named like a logo / signature fragment.
  if (att.size > 40_000) return false;
  const name = att.name.toLowerCase();
  return (
    /(?:logo|signature|sig|banner|footer|icon|spacer|pixel)/i.test(name) ||
    att.size < 8_000
  );
}
